import React, { useMemo } from 'react';
import { useRouter } from 'next/router';
import { Typography } from '@material-ui/core';

import Modal from 'components/ui/Modal';
import { useCartProducts } from 'utils/hooks/useCartProducts';
import { addToCart, substractFromCart, removeFromCart } from 'utils/apollo/vars/cartVar';
import CartProduct from './CartProduct/CartProduct';
import TotalPrice from './TotalPrice';
import CartModalActions from './CartModalActions';
import { useBuyDisabledMessage } from 'utils/hooks/useBuyDisabledMessage';
import events from 'utils/helpers/analytics/events';
import { calculateTotalPrice } from 'utils/helpers/checkout/calculateTotalPrice';
import Spinner from 'components/ui/loaders/Spinner/Spinner';
import { MIN_FREE_DELIVERY_PRICE } from 'domain/constants';

interface CartModalProps {
  open: boolean;
  onClose: () => void;
}

const CartModal: React.FC<CartModalProps> = ({ onClose, open }): JSX.Element => {
  const { push, pathname } = useRouter();

  const { products, loading, error } = useCartProducts();

  const totalPrice = useMemo(() => calculateTotalPrice(products), [products]);

  const disabledMessage = useBuyDisabledMessage(products, totalPrice);

  const handleCheckout = () => {
    onClose();

    if (pathname !== '/checkout') {
      push('/checkout');

      events.beginCheckOut(
        totalPrice,
        products.map(
          ({ price, productItemCode, quantity, name, category: { name: category } }) => ({
            price,
            productItemCode,
            quantity,
            category,
            name
          })
        )
      );
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Кошик"
      subtitle={`Безкоштовна доставка від ${MIN_FREE_DELIVERY_PRICE} грн!`}
      actionsComponent={
        <>
          <TotalPrice price={totalPrice} />

          <CartModalActions
            onClose={onClose}
            onCheckout={handleCheckout}
            disabledMessage={disabledMessage}
          />
        </>
      }
    >
      {loading ? (
        <Spinner />
      ) : (
        <>
          {error ? (
            <Typography variant="h6" color="error">
              {error}
            </Typography>
          ) : (
            products.map((product) => (
              <CartProduct
                key={product.sys.id}
                product={product}
                addToCart={addToCart}
                substractFromCart={substractFromCart}
                removeFromCart={removeFromCart}
              />
            ))
          )}
        </>
      )}
    </Modal>
  );
};

export default CartModal;
